// Client Page detail — /admin/clients/:id
// Shows: contact info (editable inline), linked properties w/ picker,
// live open WOs, recent completed jobs, big free-form notes area.

import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft, Buildings, Phone, Envelope, User as UserIcon,
  MapPin, Wrench, CheckCircle, Clock, Trash, Plus, MagnifyingGlass, X,
  ListPlus, ShareNetwork, Copy as CopyIcon, ArrowsClockwise, EyeSlash,
  QrCode, Image as ImageIcon, Palette, Eyedropper, Lightning,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { extractLogoPalette } from "@/lib/extractLogoPalette";
import QuickJobModal from "@/components/QuickJobModal";

const STATUS_COLOR = {
  pool: "bg-amber-100 text-amber-800 border-amber-300",
  assigned: "bg-sky-100 text-sky-800 border-sky-300",
  in_progress: "bg-violet-100 text-violet-800 border-violet-300",
  follow_up: "bg-orange-100 text-orange-800 border-orange-300",
  needs_quote: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function AdminClientPageDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [addingProp, setAddingProp] = useState(false);
  const [propQ, setPropQ] = useState("");
  const [propResults, setPropResults] = useState([]);
  // Bulk-link state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [quickJobOpen, setQuickJobOpen] = useState(false);
  // Branding
  const [logoBusy, setLogoBusy] = useState(false);
  const [palette, setPalette] = useState([]);   // extracted swatches from logo
  const [logoBust, setLogoBust] = useState(0);  // cache-buster after re-upload
  const [colorInput, setColorInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/client-pages/${id}`);
      setPage(r.data);
      setNotes(r.data.notes || "");
      setNotesDirty(false);
      setColorInput(r.data.brand_color || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // ── Branding: logo upload + auto color extraction ─────────────────
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      // Extract palette in parallel with upload — user sees swatches fast.
      const extractP = extractLogoPalette(file).catch(() => null);
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/client-pages/${id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const pal = await extractP;
      setLogoBust(Date.now());
      if (pal && pal.dominant) {
        setPalette(pal.palette);
        // Auto-apply the dominant color as the brand color.
        setColorInput(pal.dominant);
        await patch({ brand_color: pal.dominant });
        toast.success("Logo uploaded + brand color picked");
      } else {
        setPage(prev => ({ ...prev, has_logo: true }));
        toast.success("Logo uploaded");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove logo?")) return;
    try {
      await api.delete(`/client-pages/${id}/logo`);
      setPage(prev => ({ ...prev, has_logo: false }));
      setPalette([]);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Remove failed");
    }
  };

  const applyColor = async (hex) => {
    const v = (hex || "").trim();
    if (v && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) {
      toast.error("Use a hex like #059669");
      return;
    }
    setColorInput(v);
    await patch({ brand_color: v });
  };

  const patch = async (partial) => {
    setSavingField(Object.keys(partial)[0]);
    try {
      const r = await api.patch(`/client-pages/${id}`, partial);
      setPage(prev => ({ ...prev, ...r.data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSavingField(null); }
  };

  const saveNotes = async () => {
    await patch({ notes });
    setNotesDirty(false);
    toast.success("Notes saved");
  };

  const searchProperties = async (term) => {
    setPropQ(term);
    if (term.length < 2) { setPropResults([]); return; }
    try {
      const r = await api.get(`/client-pages/_lookup/properties`, { params: { q: term } });
      setPropResults(r.data.properties || []);
    } catch { /* silent */ }
  };

  const addProperty = async (addressKey) => {
    const next = [...(page.linked_property_keys || []), addressKey];
    await patch({ linked_property_keys: next });
    setAddingProp(false);
    setPropQ("");
    setPropResults([]);
    load(); // reload to refresh linked_properties + WOs
  };

  const removeProperty = async (addressKey) => {
    const next = (page.linked_property_keys || []).filter(k => k !== addressKey);
    await patch({ linked_property_keys: next });
    load();
  };

  const runBulkLink = async () => {
    const terms = bulkText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (terms.length === 0) { toast.error("Paste at least one address"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post(`/client-pages/${id}/bulk-link`, { terms });
      setBulkReport(r.data);
      if (r.data.added > 0) {
        toast.success(`Linked ${r.data.added} properties`);
        load();
      } else if (r.data.resolved.length === 0 && r.data.ambiguous.length === 0) {
        toast.error("No matches found");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk link failed");
    } finally { setBulkBusy(false); }
  };

  const linkAmbiguousChoice = async (term, addressKey) => {
    try {
      const r = await api.patch(`/client-pages/${id}`, {
        linked_property_keys: [...(page.linked_property_keys || []), addressKey],
      });
      setPage(prev => ({ ...prev, ...r.data }));
      // Remove that term from the ambiguous list
      setBulkReport(prev => ({
        ...prev,
        ambiguous: prev.ambiguous.filter(a => a.term !== term),
        added: prev.added + 1,
      }));
      load();
      toast.success("Linked");
    } catch { toast.error("Link failed"); }
  };

  const linkAllAmbiguous = async (term, candidates) => {
    try {
      const newKeys = candidates
        .map(c => c.address_key)
        .filter(k => !(page.linked_property_keys || []).includes(k));
      const merged = [...(page.linked_property_keys || []), ...newKeys];
      const r = await api.patch(`/client-pages/${id}`, {
        linked_property_keys: merged,
      });
      setPage(prev => ({ ...prev, ...r.data }));
      setBulkReport(prev => ({
        ...prev,
        ambiguous: prev.ambiguous.filter(a => a.term !== term),
        added: prev.added + newKeys.length,
      }));
      load();
      toast.success(`Linked all ${newKeys.length}`);
    } catch { toast.error("Link failed"); }
  };

  // Portal helpers
  const portalUrl = page?.portal_token
    ? `${window.location.origin}/portal/client/${page.portal_token}`
    : "";
  const enablePortal = async () => {
    try {
      const r = await api.post(`/client-pages/${id}/portal/enable`, {});
      setPage(prev => ({ ...prev, portal_enabled: true, portal_token: r.data.portal_token }));
      toast.success("Public portal enabled");
    } catch { toast.error("Failed to enable"); }
  };
  const disablePortal = async () => {
    if (!confirm("Disable the public portal? The current link will stop working.")) return;
    try {
      await api.post(`/client-pages/${id}/portal/disable`, {});
      setPage(prev => ({ ...prev, portal_enabled: false, portal_token: null }));
      toast.success("Portal disabled");
    } catch { toast.error("Failed to disable"); }
  };
  const rotatePortal = async () => {
    if (!confirm("Rotate the token? Old link stops working immediately.")) return;
    try {
      const r = await api.post(`/client-pages/${id}/portal/rotate`, {});
      setPage(prev => ({ ...prev, portal_enabled: true, portal_token: r.data.portal_token }));
      toast.success("New link minted");
    } catch { toast.error("Failed to rotate"); }
  };

  const archive = async () => {
    if (!confirm("Archive this client page? It won't appear in the list anymore.")) return;
    try {
      await api.delete(`/client-pages/${id}`);
      toast.success("Archived");
      nav("/admin/clients");
    } catch (e) {
      toast.error("Archive failed");
    }
  };

  const alreadyLinked = useMemo(
    () => new Set(page?.linked_property_keys || []),
    [page]
  );

  if (loading) return <AdminShell><div className="p-8 text-center text-slate-500">Loading…</div></AdminShell>;
  if (!page) return <AdminShell><div className="p-8 text-center text-slate-500">Not found</div></AdminShell>;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="client-page-detail">
        <Link to="/admin/clients" data-testid="back-to-clients"
          className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900">
          <ArrowLeft size={12}/> Back to Clients
        </Link>

        {/* Header */}
        <div className="mt-3 rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-950 text-white p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
              page.is_org ? "bg-violet-500/20 text-violet-200" : "bg-emerald-500/20 text-emerald-200"
            }`}>
              {page.is_org ? <Buildings weight="fill" size={26}/> : <UserIcon weight="fill" size={26}/>}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-black text-3xl sm:text-4xl leading-none tracking-tight">
                {page.name}
              </h1>
              {page.org_name && !page.is_org && (
                <div className="mt-1 text-sm text-white/70">{page.org_name}</div>
              )}
              {page.role && <div className="text-xs text-white/60 italic">{page.role}</div>}
            </div>
            <button onClick={archive}
              data-testid="archive-client-btn"
              className="h-8 px-2 border border-white/20 text-white/60 hover:text-white hover:bg-white/10 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest">
              <Trash size={11}/> Archive
            </button>
          </div>
        </div>

        {/* Contact card */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="contact-card">
          <div className="font-black uppercase text-[11px] tracking-widest text-slate-500 mb-3">Contact</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ContactField label="Name" value={page.name} onSave={(v) => patch({ name: v })} testid="edit-name" saving={savingField === "name"}/>
            <ContactField label="Organization" value={page.org_name} onSave={(v) => patch({ org_name: v })} testid="edit-org" saving={savingField === "org_name"}/>
            <ContactField label="Role" value={page.role} onSave={(v) => patch({ role: v })} testid="edit-role" saving={savingField === "role"}/>
            <ContactField label="Phone" value={page.phone} onSave={(v) => patch({ phone: v })} testid="edit-phone" saving={savingField === "phone"} icon={<Phone size={12}/>}/>
            <ContactField label="Email" value={page.email} onSave={(v) => patch({ email: v })} testid="edit-email" saving={savingField === "email"} icon={<Envelope size={12}/>} type="email"/>
          </div>
        </div>

        {/* Public portal (share link) */}
        <div className="mt-4 border-2 border-cyan-300 bg-cyan-50 p-4 rounded-lg" data-testid="portal-panel">
          <div className="flex items-center gap-2 mb-2">
            <ShareNetwork weight="fill" size={14} className="text-cyan-700"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-cyan-900">
              Public Portal Link
            </div>
            <div className={`ml-auto inline-flex items-center gap-1 border px-1.5 py-0.5 font-black uppercase text-[9px] tracking-widest ${
              page.portal_enabled
                ? "border-emerald-400 bg-white text-emerald-800"
                : "border-slate-300 bg-white text-slate-500"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full inline-block ${
                page.portal_enabled ? "bg-emerald-500" : "bg-slate-300"
              }`}/>
              {page.portal_enabled ? "Live" : "Off"}
            </div>
          </div>
          {!page.portal_enabled ? (
            <>
              <p className="text-xs text-cyan-900/80">
                Generate a read-only public link so {page.name || "this client"} can bookmark it — no login needed. They'll see contact info, linked addresses, open WOs, and recent completed jobs. No internal notes, no PII.
              </p>
              <button
                onClick={enablePortal}
                data-testid="enable-portal-btn"
                className="mt-2 h-9 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[11px] tracking-widest">
                Enable public link
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="flex-1 min-w-0 truncate bg-white border-2 border-cyan-300 px-2 py-1.5 font-mono text-xs text-slate-900"
                  data-testid="portal-url">
                  {portalUrl}
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Copied!"); }}
                  data-testid="copy-portal-url"
                  className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                  <CopyIcon size={12}/> Copy
                </button>
                <a href={portalUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="preview-portal"
                  className="h-9 px-3 border-2 border-cyan-500 bg-white text-cyan-700 hover:bg-cyan-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                  Preview
                </a>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10px]">
                <button onClick={rotatePortal}
                  data-testid="rotate-portal-btn"
                  className="text-cyan-700 hover:text-cyan-900 font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <ArrowsClockwise size={11}/> Rotate token
                </button>
                <button onClick={() => setQrOpen(v => !v)}
                  data-testid="toggle-qr-btn"
                  className="text-cyan-700 hover:text-cyan-900 font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <QrCode size={11}/> {qrOpen ? "Hide" : "Show"} QR
                </button>
                <button onClick={disablePortal}
                  data-testid="disable-portal-btn"
                  className="text-rose-600 hover:text-rose-800 font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <EyeSlash size={11}/> Disable
                </button>
              </div>

              {qrOpen && (
                <div className="mt-3 flex flex-col items-center gap-2 p-3 bg-white border-2 border-cyan-300 rounded"
                  data-testid="portal-qr-panel">
                  <QRCodeSVG value={portalUrl}
                    size={200}
                    level="M"
                    fgColor="#0f172a"
                    bgColor="#ffffff"
                    marginSize={2}
                    data-testid="portal-qr-svg"/>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-center max-w-xs">
                    Point {page.name || "their"} phone camera at this code — bookmarks the portal in one tap
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Branding — per-client logo + brand color used on the public portal */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="branding-panel">
          <div className="flex items-center gap-2 mb-3">
            <Palette weight="fill" size={14} className="text-slate-500"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Portal Branding
            </div>
            <div className="ml-auto text-[9px] font-black uppercase tracking-widest text-slate-400">
              Shown on {page.name || "the"} public portal
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Logo upload */}
            <div className="flex flex-col items-center gap-2">
              <div
                data-testid="branding-logo-preview"
                className="w-24 h-24 border-2 border-dashed border-slate-300 rounded flex items-center justify-center bg-slate-50 overflow-hidden">
                {page.has_logo ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/client-pages/${id}/logo${logoBust ? `?v=${logoBust}` : ""}`}
                    alt=""
                    className="max-w-full max-h-full object-contain"/>
                ) : (
                  <ImageIcon size={28} className="text-slate-400"/>
                )}
              </div>
              <label
                data-testid="branding-logo-upload"
                className={`h-8 px-3 inline-flex items-center gap-1 cursor-pointer font-black uppercase text-[10px] tracking-widest ${
                  logoBusy ? "bg-slate-300 text-slate-500" : "bg-slate-800 hover:bg-slate-900 text-white"
                }`}>
                <ImageIcon size={11}/> {page.has_logo ? "Replace" : "Upload"}
                <input type="file" accept="image/png,image/jpeg,image/webp"
                  disabled={logoBusy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo(f);
                    e.target.value = "";
                  }}
                  className="hidden"/>
              </label>
              {page.has_logo && (
                <button
                  onClick={removeLogo}
                  data-testid="branding-logo-remove"
                  className="text-[10px] text-rose-600 hover:text-rose-800 font-black uppercase tracking-widest">
                  Remove
                </button>
              )}
            </div>

            {/* Color: auto-picked from logo, editable */}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                Brand color {palette.length ? "· picked from logo" : ""}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-10 h-10 rounded border-2 border-slate-300 flex-shrink-0"
                  style={{ background: colorInput || "#e2e8f0" }}
                  data-testid="branding-color-swatch"/>
                <input type="color"
                  value={colorInput || "#0f172a"}
                  onChange={e => setColorInput(e.target.value)}
                  onBlur={e => applyColor(e.target.value)}
                  data-testid="branding-color-picker"
                  className="w-10 h-10 border-2 border-slate-300 cursor-pointer p-0 bg-white"/>
                <input
                  type="text"
                  value={colorInput}
                  placeholder="#059669"
                  onChange={e => setColorInput(e.target.value)}
                  onBlur={e => applyColor(e.target.value)}
                  data-testid="branding-color-hex"
                  className="flex-1 h-10 px-2 border-2 border-slate-300 font-mono text-xs"/>
                {colorInput && (
                  <button
                    onClick={() => applyColor("")}
                    data-testid="branding-color-clear"
                    className="text-[10px] text-slate-500 hover:text-slate-800 font-black uppercase tracking-widest">
                    Clear
                  </button>
                )}
              </div>

              {palette.length > 1 && (
                <div className="mb-2">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 inline-flex items-center gap-1">
                    <Eyedropper size={10}/> Other colors in your logo
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {palette.map((hex, i) => (
                      <button key={i}
                        onClick={() => applyColor(hex)}
                        data-testid={`branding-palette-swatch-${i}`}
                        title={hex}
                        className={`w-7 h-7 rounded-full border-2 ${
                          colorInput?.toLowerCase() === hex.toLowerCase()
                            ? "border-slate-900 ring-2 ring-slate-900/20"
                            : "border-slate-300"
                        }`}
                        style={{ background: hex }}/>
                    ))}
                  </div>
                </div>
              )}

              {/* Live preview strip so the operator sees what the portal will look like */}
              <div className="mt-2 rounded overflow-hidden border-2 border-slate-200" data-testid="branding-preview">
                <div
                  className="px-3 py-3 flex items-center gap-2 text-white"
                  style={{
                    background: colorInput
                      ? `linear-gradient(135deg, ${colorInput}, ${colorInput}dd 55%, #0f172a)`
                      : "linear-gradient(135deg, #0f172a, #065f46, #020617)",
                  }}>
                  {page.has_logo && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/client-pages/${id}/logo${logoBust ? `?v=${logoBust}` : ""}`}
                      alt=""
                      className="w-10 h-10 rounded bg-white/95 p-1 object-contain flex-shrink-0"/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-black uppercase tracking-widest opacity-80">
                      Field Radar · Client Portal
                    </div>
                    <div className="font-display font-black text-lg leading-tight truncate">
                      {page.name || "Client"}
                    </div>
                  </div>
                </div>
                <div className="p-2 bg-slate-50 flex justify-end">
                  <button
                    style={{ background: colorInput || "#0f172a" }}
                    className="h-7 px-3 text-white font-black uppercase text-[10px] tracking-widest">
                    Open Form
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Linked properties */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="linked-properties">
          <div className="flex items-center gap-2 mb-3">
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Linked Properties · {(page.linked_properties || []).length}
            </div>
            <button
              onClick={() => setBulkOpen(v => !v)}
              data-testid="bulk-link-btn"
              className="ml-auto h-7 px-2 border-2 border-slate-300 bg-white text-slate-700 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50 inline-flex items-center gap-1">
              <ListPlus size={11}/> Bulk Add
            </button>
            <button
              onClick={() => setAddingProp(v => !v)}
              data-testid="add-property-btn"
              className="h-7 px-2 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 font-black uppercase text-[10px] tracking-widest hover:bg-emerald-100 inline-flex items-center gap-1">
              <Plus size={11}/> Add One
            </button>
          </div>

          {/* Bulk-link panel */}
          {bulkOpen && (
            <div className="mb-3 p-3 border-2 border-slate-300 bg-slate-50" data-testid="bulk-link-panel">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                Paste addresses — one per line (or comma-separated)
              </div>
              <textarea
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                placeholder={`e.g.:\n123 Main St\n456 Oak Ave\n789 Elm Ln`}
                data-testid="bulk-link-textarea"
                className="w-full min-h-[100px] p-2 border-2 border-slate-300 text-xs font-mono"/>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={runBulkLink}
                  disabled={bulkBusy || !bulkText.trim()}
                  data-testid="bulk-link-run"
                  className="h-8 px-3 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
                  {bulkBusy ? "Working…" : "Resolve & Link"}
                </button>
                <button
                  onClick={() => { setBulkOpen(false); setBulkText(""); setBulkReport(null); }}
                  className="h-8 px-3 border-2 border-slate-300 text-slate-700 font-black uppercase text-[10px] tracking-widest hover:bg-slate-100">
                  Cancel
                </button>
              </div>

              {/* Report */}
              {bulkReport && (
                <div className="mt-3 space-y-2 text-xs" data-testid="bulk-link-report">
                  <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800">
                    {bulkReport.added} newly linked · {bulkReport.already_linked} already linked
                  </div>
                  {bulkReport.ambiguous.length > 0 && (
                    <div className="p-2 border-2 border-amber-300 bg-amber-50">
                      <div className="font-black uppercase text-[10px] tracking-widest text-amber-900 mb-1">
                        Multiple matches — pick which one
                      </div>
                      {bulkReport.ambiguous.map((a, i) => (
                        <div key={i} className="mt-2">
                          <div className="flex items-center gap-2">
                            <div className="text-[11px] font-black flex-1">"{a.term}" — {a.candidates.length} matches</div>
                            <button
                              onClick={() => linkAllAmbiguous(a.term, a.candidates)}
                              data-testid={`ambig-link-all-${i}`}
                              className="h-6 px-2 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[9px] tracking-widest">
                              Link all {a.candidates.length}
                            </button>
                          </div>
                          <div className="mt-1 space-y-0.5">
                            {a.candidates.slice(0, 8).map(c => (
                              <button
                                key={c.address_key}
                                onClick={() => linkAmbiguousChoice(a.term, c.address_key)}
                                data-testid={`ambig-pick-${c.address_key}`}
                                className="block w-full text-left px-2 py-1 text-[11px] bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-100">
                                {c.address}{c.unit ? ` · Unit ${c.unit}` : ""}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {bulkReport.unresolved.length > 0 && (
                    <div className="p-2 border-2 border-rose-300 bg-rose-50">
                      <div className="font-black uppercase text-[10px] tracking-widest text-rose-900 mb-1">
                        Not found ({bulkReport.unresolved.length})
                      </div>
                      <ul className="pl-4 list-disc text-[11px]">
                        {bulkReport.unresolved.map((u, i) => <li key={i}>"{u}"</li>)}
                      </ul>
                      <div className="mt-2 text-[10px] text-rose-900/70">
                        These addresses aren't in your property list yet. Add them via
                        {" "}<Link to="/admin/portfolios" className="underline font-black">Portfolios → your portfolio → Bulk Audit</Link>{" "}
                        first, then re-run bulk link.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {addingProp && (
            <div className="mb-3 p-2 border-2 border-emerald-200 bg-emerald-50">
              <div className="flex items-center gap-2">
                <MagnifyingGlass size={13} className="text-slate-400"/>
                <input
                  value={propQ} onChange={e => searchProperties(e.target.value)}
                  placeholder="Type to search — min 2 characters"
                  autoFocus
                  data-testid="property-search-input"
                  className="flex-1 h-8 px-2 border border-slate-300 text-xs"/>
                <button onClick={() => { setAddingProp(false); setPropQ(""); setPropResults([]); }}>
                  <X size={14} className="text-slate-500"/>
                </button>
              </div>
              {propResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto space-y-1" data-testid="property-search-results">
                  {propResults.map(p => (
                    <button
                      key={p.address_key}
                      onClick={() => addProperty(p.address_key)}
                      disabled={alreadyLinked.has(p.address_key)}
                      data-testid={`property-result-${p.address_key}`}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded ${
                        alreadyLinked.has(p.address_key)
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-white border border-transparent hover:border-emerald-400"
                      }`}>
                      <div className="font-black">{p.address}</div>
                      {p.unit && <div className="text-[10px] text-slate-500">Unit {p.unit}</div>}
                      {alreadyLinked.has(p.address_key) && <div className="text-[10px] italic text-slate-500">already linked</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(page.linked_properties || []).length === 0 && !addingProp ? (
            <div className="text-xs text-slate-400 italic py-3 text-center">
              No properties linked yet. Tap "Add Property" to search and link.
            </div>
          ) : (
            <>
              <button
                onClick={() => setPropsOpen(v => !v)}
                data-testid="admin-linked-toggle"
                className="w-full flex items-center gap-2 px-2 py-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded text-xs font-black uppercase tracking-widest text-slate-700">
                <MapPin size={12} className="text-slate-500"/>
                <span>{(page.linked_properties || []).length} linked</span>
                <span className="ml-auto text-[10px] text-slate-500">{propsOpen ? "Hide" : "Show all"}</span>
              </button>
              {propsOpen && (
                <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                  {(page.linked_properties || []).map(p => (
                    <div key={p.address_key} className="flex items-center gap-2 px-2 py-1.5 border border-slate-100 hover:bg-slate-50 rounded"
                      data-testid={`linked-prop-${p.address_key}`}>
                      <MapPin size={12} className="text-slate-400 flex-shrink-0"/>
                      <div className="flex-1 text-xs">
                        <span className="font-black">{p.address}</span>
                        {p.unit && <span className="text-slate-500"> · Unit {p.unit}</span>}
                      </div>
                      <button
                        onClick={() => removeProperty(p.address_key)}
                        data-testid={`unlink-${p.address_key}`}
                        className="opacity-40 hover:opacity-100 text-slate-500 hover:text-rose-600">
                        <X size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Open WOs */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="open-wos-panel">
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={14} className="text-slate-500"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Open Work Orders · {(page.open_wos || []).length}
            </div>
            {(() => {
              // Pre-fill address: if the client has a single unique linked
              // property address, hand it to Quick Job. Chew Flats (43 units
              // all at 5346 Chew Ave) → address auto-fills, Andrew just
              // types the unit inside the modal.
              const addrs = [...new Set((page.linked_properties || [])
                .map(p => (p.address || "").trim())
                .filter(Boolean))];
              const preAddr = addrs.length === 1 ? addrs[0] : "";
              return (
                <button
                  onClick={() => setQuickJobOpen(true)}
                  data-testid="client-quick-job-btn"
                  className="ml-auto h-8 px-3 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-white font-black uppercase text-[10px] tracking-widest rounded"
                  title={preAddr ? `Pre-fills ${preAddr}` : "Log a job at one of the linked properties"}>
                  <Lightning weight="fill" size={11}/> Quick Job
                </button>
              );
            })()}
          </div>
          {(page.open_wos || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-2 text-center">No open work orders on linked properties.</div>
          ) : (
            <div className="space-y-1.5">
              {page.open_wos.map(j => (
                <Link key={j.id} to={`/admin/jobs/${j.id}`}
                  data-testid={`open-wo-${j.id}`}
                  className="flex items-start gap-2 px-2 py-1.5 border border-slate-100 hover:bg-slate-50 hover:border-slate-300 rounded transition">
                  <span className={`inline-flex items-center px-1.5 py-0.5 border text-[9px] font-black uppercase tracking-widest ${STATUS_COLOR[j.status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                    {j.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-[12px] text-slate-900 truncate">{j.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {j.address}{j.unit ? ` · ${j.unit}` : ""}
                      {j.assigned_to_name && <span className="ml-2">· {j.assigned_to_name}</span>}
                      {j.work_order_number && <span className="ml-2 font-mono">WO#{j.work_order_number}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Related clients — other client pages sharing at least one property */}
        {page.related_clients && page.related_clients.length > 0 && (
          <div className="mt-4 border-2 border-violet-300 bg-violet-50 p-4 rounded-lg" data-testid="related-clients-panel">
            <div className="font-black uppercase text-[11px] tracking-widest text-violet-900 mb-2">
              Related Clients · {page.related_clients.length}
            </div>
            <div className="text-[10px] text-violet-900/70 mb-2 italic">
              Other pages sharing at least one property with this client — jump to coordinate.
            </div>
            <div className="flex flex-wrap gap-2">
              {page.related_clients.map(rc => (
                <Link key={rc.id} to={`/admin/clients/${rc.id}`}
                  data-testid={`related-client-${rc.id}`}
                  className="inline-flex items-center gap-2 border-2 border-violet-300 bg-white hover:border-violet-500 hover:bg-violet-100 px-2 py-1.5 rounded text-xs group">
                  {rc.is_org ? <Buildings size={12} className="text-violet-600"/> : <UserIcon size={12} className="text-emerald-600"/>}
                  <div>
                    <div className="font-black text-slate-900">{rc.name}</div>
                    {rc.org_name && !rc.is_org && (
                      <div className="text-[10px] text-slate-500">{rc.org_name}</div>
                    )}
                    {rc.role && <div className="text-[10px] text-slate-500 italic">{rc.role}</div>}
                  </div>
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 border border-violet-400 bg-violet-100 text-violet-800 font-black text-[9px]">
                    {rc.shared_property_count} shared
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent completed */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="recent-completed-panel">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={14} className="text-emerald-600"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Recent Completed · {(page.recent_completed || []).length}
            </div>
          </div>
          {(page.recent_completed || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-2 text-center">No recently completed jobs.</div>
          ) : (
            <div className="space-y-1.5">
              {page.recent_completed.map(j => (
                <Link key={j.id} to={`/admin/jobs/${j.id}`}
                  data-testid={`completed-wo-${j.id}`}
                  className="flex items-start gap-2 px-2 py-1.5 border border-slate-100 hover:bg-slate-50 rounded transition">
                  <CheckCircle size={12} className="text-emerald-500 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-slate-800 truncate">{j.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {j.address}{j.unit ? ` · ${j.unit}` : ""}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-4 border-2 border-slate-200 bg-white p-4 rounded-lg" data-testid="notes-panel">
          <div className="flex items-center gap-2 mb-2">
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">Notes</div>
            {notesDirty && (
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">Unsaved</span>
            )}
            <button
              onClick={saveNotes}
              disabled={!notesDirty}
              data-testid="save-notes"
              className="ml-auto h-7 px-3 bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-30 hover:bg-slate-800">
              Save
            </button>
          </div>
          <textarea
            value={notes}
            onChange={e => { setNotes(e.target.value); setNotesDirty(true); }}
            placeholder="Preferences, quirks, gate codes, relationship intel, do's & don'ts…"
            data-testid="notes-textarea"
            className="w-full min-h-[160px] p-3 border-2 border-slate-200 focus:border-slate-400 outline-none text-sm font-mono leading-relaxed"/>
        </div>
      </div>

      {/* Quick Job modal — pre-fills the address if the client has a
          single unique linked property, so Chew Flats (43 units all at
          5346 Chew Ave) auto-fills that address and Andrew just types
          the unit. */}
      <QuickJobModal
        open={quickJobOpen}
        onClose={() => { setQuickJobOpen(false); load(); }}
        initialAddress={(() => {
          const addrs = [...new Set((page.linked_properties || [])
            .map(p => (p.address || "").trim())
            .filter(Boolean))];
          return addrs.length === 1 ? addrs[0] : "";
        })()}/>
    </AdminShell>
  );
}

function ContactField({ label, value, onSave, testid, saving, icon, type = "text" }) {
  const [v, setV] = useState(value || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-0.5 flex items-center gap-1">
        {icon}{label}
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input
            type={type}
            value={v} onChange={e => setV(e.target.value)}
            autoFocus
            onBlur={async () => { setEditing(false); if (v !== value) await onSave(v); }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") { setEditing(false); if (v !== value) await onSave(v); }
              if (e.key === "Escape") { setV(value || ""); setEditing(false); }
            }}
            data-testid={testid}
            className="flex-1 h-8 px-2 border-2 border-emerald-400 text-sm"/>
          {saving && <span className="text-[10px] text-slate-400">saving…</span>}
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          data-testid={`${testid}-view`}
          className="w-full text-left px-2 py-1.5 border border-transparent hover:border-slate-300 hover:bg-slate-50 rounded text-sm truncate min-h-[32px]">
          {value || <span className="text-slate-400 italic">— tap to add —</span>}
        </button>
      )}
    </div>
  );
}
